<script setup lang="ts">
import {computed, ref, watchEffect, defineExpose, watch} from "vue";
import {Model} from "@/app/Model";

// define component props for the slider component
let { active, min, max, step, minValue, maxValue, changeFunct, cid } = defineProps({
  active: {
    type: Boolean,
    default: false,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  minValue: {
    type: Number,
    default: 50,
  },
  maxValue: {
    type: Number,
    default: 80,
  },
  changeFunct: {
    type: Function,
    default: () => {},
  },
  cid: {
    type: String,
    default: "",
  }
});

let model: Model = Model.getInstance();
// define emits for the slider component
const emit = defineEmits(["update:minValue", "update:maxValue", "update:active"]);

// define refs for the slider element and the slider values
const slider = ref(null);
const inputMin = ref(null);
const inputMax = ref(null);
const sliderMinValue = ref(minValue);
const sliderMaxValue = ref(maxValue);
const sliderActive = ref(active);

// function to get the percentage of a value between the min and max values
const getPercent = (value:number, min:number, max:number): number => {
  let perc: number = ((value - min) / (max - min)) * 100;
  if (perc < 0) perc = 0;
  if (perc > 100) perc = 100;
  return perc;
};

// function to get the difference between the min and max values
const sliderDifference = computed(() => {
  return Math.abs(sliderMaxValue.value - sliderMinValue.value);
});

// function to set the css variables for width, left and right
const setCSSProps = (left:number, right:number): void => {
  if (slider.value) {
    //@ts-ignore
    slider.value.style.setProperty("--progressLeft_"+cid.replace("2",""), `${left}%`);
    //@ts-ignore
    slider.value.style.setProperty("--progressRight_"+cid.replace("2",""), `${right}%`);
  }
};

// watchEffect to emit the updated values, and update the css variables
// when the slider values change
watchEffect(() => {
  if (slider.value) {
    updateProps();

    // emit slider values when updated
    emit("update:minValue", sliderMinValue.value);
    emit("update:maxValue", sliderMaxValue.value);
  }

});
const updateProps = (): void => {
  setInactive();
  // calculate values in percentages
  const leftPercent = getPercent(sliderMinValue.value, min, max);
  const rightPercent = 100 - getPercent(sliderMaxValue.value, min, max);

  // set the CSS variables
  if (!isNaN(leftPercent) && !isNaN(rightPercent)) setCSSProps(leftPercent, rightPercent);
}
// validation sliderMinValue do not greater than sliderMaxValue and opposite
//@ts-ignore
const onInput = ({ target }) => {
  if (target.name === 'min') {
    target.value > sliderMaxValue.value
        ? target.value = sliderMaxValue.value
        : sliderMinValue.value = parseFloat(target.value);
  }

  if (target.name === 'max') {
    target.value < sliderMinValue.value
        ? target.value = sliderMinValue.value
        : sliderMaxValue.value = parseFloat(target.value);
  }
};

function manageActive(value:boolean): void {
  sliderActive.value = value;
  model.pen_flow_percorso.importo_evaso_active = value;
  //console.log("manageActive", "value", value, "sliderActive.value", sliderActive.value, model.pen_flow_percorso.importo_evaso_active)
  changeFunct(sliderActive.value, cid.replace("2",""));
  emit("update:active", value);
}
function setInactive(): void {
  let activePre: boolean = sliderActive.value;
  //sliderActive.value = false;
  if (activePre) {
    //emit("update:active", sliderActive.value);
   // changeFunct(sliderActive.value, cid);
  }
}

//expose
function updateMinMaxSlider(vmin?: number, vmax?: number, vactive?: boolean): void {
  //console.log("UPDATE", cid)
  if (vmin) {
    if (vmin < min) vmin = min;
    sliderMinValue.value = vmin;
    minValue = vmin;
  }
  if (vmax) {
    if (vmax > max) vmax = max;
    sliderMaxValue.value = vmax;
    maxValue = vmax;
  }

  if ( vactive != undefined) {
    if (vactive === true || vactive === false) sliderActive.value = vactive;
  } else {
    //@ts-ignore
    sliderActive.value = vactive; //model.pen_flow_percorso.importo_evaso_active;
  }
  if (slider.value) updateProps();
}
defineExpose({
  updateMinMaxSlider
});

function renderMin(value: number): string {
  if (sliderMinValue.value < value) {
    minValue = value;
    sliderMinValue.value = value;
  }
  if (sliderMaxValue.value < value) {
    maxValue = value;
    sliderMaxValue.value = value;
  }
  if (slider.value) updateProps();
  return value.toString()
}
function renderMax(value: number): string {
  if (sliderMaxValue.value > value) {
    maxValue = value;
    sliderMaxValue.value = value;
  }
  if (slider.value) updateProps();
  return value.toString()
}
</script>
<template>
  <div ref="slider" class="custom-slider minmax" :id="'input_'+cid">
    <div class="minmax-indicator"></div>
    <input
        ref="inputMin"
        type="range"
        name="min"
        id="min"
        :min="min"
        :max="max"
        :value="minValue"
        :step="step"
        @input="onInput"
    />
    <input
        ref="inputMax"
        type="range"
        name="max"
        id="max"
        :min="min"
        :max="max"
        :value="maxValue"
        :step="step"
        @input="onInput"
    />
  </div>
  <div class="minmax-inputs">
    <input type="number" :step="step" v-model="sliderMinValue" />
    <input type="number" :step="step" v-model="sliderMaxValue" />
  </div>
  <div class="row minmax-actions">
    <div class="col-7 info-ranges"> Min: {{ renderMin(min) }}<br>Max:{{ renderMax(max) }} </div>
    <div class="col-5 form-check form-switch">
      <button type="button" @click="manageActive(true)" class="btn btn-sm btn-primary btn-for-slider" v-if="!sliderActive">Applica</button>
      <button type="button" @click="manageActive(false)" class="btn btn-sm btn-danger btn-for-slider" v-else>Rimuovi</button>
    </div>
  </div>
</template>
